"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Car, Package, Wallet, ArrowRight, ShieldCheck, Zap, Smartphone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Home Refinancing",
  description: "Optimize your financial position by securing lower interest rates or tapping into your home's equity for major life goals.",
  icon: Zap,
  link: "/LendingSolutionsPage",
  features: ["Rate Reduction", "Cash-Out Options"]
}, {
  title: "Residential Mortgages",
  description: "Tailored financing solutions for first-time buyers and seasoned investors, designed to make homeownership a seamless reality.",
  icon: Package,
  link: "/LendingSolutionsPage",
  features: ["Fixed & ARM", "Low Down Payment"]
}, {
  title: "Home Equity Lines",
  description: "Leverage the value of your property with flexible credit lines that provide liquidity whenever your family needs it most.",
  icon: Wallet,
  link: "/LendingSolutionsPage",
  features: ["Flexible Access", "Interest-Only"]
}];
const stats = [{
  value: "4.25%",
  label: "Avg. 30-Year Fixed"
}, {
  value: "$2.4B",
  label: "Loans Originated"
}, {
  value: "15k+",
  label: "Families Served"
}];
export default function OurLendingExpertise() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: statsRef,
    isVisible: isStatsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262138" id="our-lending-expertise" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] -mr-[400px] -mt-[400px] rounded-lg border border-primary/5 opacity-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] -ml-[300px] -mb-[300px] rounded-lg border border-primary/5 opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px] rounded-lg border border-primary/5 opacity-5 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-6 border-primary/20 text-primary bg-primary/5 px-3 py-1 text-sm font-mono uppercase tracking-widest">
            Lending Solutions
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold font-default tracking-tight mb-6 text-primary">
            Guided Clarity in <span className="text-secondary">Finance</span>
          </h2>
          <p className="text-lg text-foreground/80 leading-relaxed font-default">
            Beacon Finance Group provides the architectural foundation for your homeownership journey, blending traditional stability with modern fintech precision.
          </p>
        </div>

        {}
        <div ref={statsRef} className={`grid grid-cols-2 md:grid-cols-3 gap-8 mb-20 pb-16 border-b border-border transition-all duration-700 delay-200 ease-out ${isStatsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {stats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col gap-2">
              <span className="text-4xl md:text-5xl font-bold font-default text-primary tracking-tighter">
                {stat.value}
              </span>
              <span className="text-sm md:text-base text-foreground/60 font-mono uppercase tracking-wider">
                {stat.label}
              </span>
            </div>)}
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-700 delay-300 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={i} data-index={i} className="group bg-white border-none shadow-md hover:shadow-4xl transition-all duration-500 overflow-hidden flex flex-col h-full rounded-md">
                <CardHeader className="pb-4">
                  <div className="w-12 h-12 rounded-md bg-primary/5 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-500">
                    <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-500 stroke-[1.5px]" />
                  </div>
                  <CardTitle className="text-xl font-bold font-default text-primary group-hover:text-secondary transition-colors duration-300">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col">
                  <CardDescription className="text-foreground/70 text-base leading-relaxed mb-6 flex-grow font-default">
                    {service.description}
                  </CardDescription>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    {service.features.map((feature, idx) => <span key={idx} data-index={idx} className="inline-flex items-center text-xs font-medium text-primary/70 bg-muted px-2.5 py-1 rounded-sm border border-primary/5">
                        <ShieldCheck className="w-3 h-3 mr-1.5 text-secondary" />
                        {feature}
                      </span>)}
                  </div>

                  <Button asChild variant="link" className="p-0 h-auto text-primary font-bold uppercase tracking-wider text-xs justify-start group/btn mt-auto">
                    <Link to={service.link}>
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>;
        })}
        </div>

      </div>
    </section>;
}